'use client';

import type { PublicPageContent, PageSection } from '@/types/public-site';
import { HeroSection } from './sections/hero-section';
import { FeaturesSection } from './sections/features-section';
import { PricingSection } from './sections/pricing-section';
import { TestimonialsSection } from './sections/testimonials-section';
import { StatsSection } from './sections/stats-section';
import { CTASection } from './sections/cta-section';
import { LogoCloudSection } from './sections/logo-cloud-section';
import { FAQSection } from './sections/faq-section';
import { ContentSection } from './sections/content-section';
import { FormSection } from './sections/form-section';
import { NewsletterSection } from './sections/newsletter-section';
import { WhitepaperDownloadSection } from './sections/whitepaper-download-section';
import { ObjectionHandlingSection } from './sections/objection-handling-section';
import { TrustStackSection } from './sections/trust-stack-section';
import { HowItWorksSection } from './sections/how-it-works';

interface PageRendererProps {
  content: PublicPageContent;
  locale: string;
  currency?: string;
}

export function PageRenderer({ content, locale, currency = 'USD' }: PageRendererProps) {
  const { sections } = content;

  const renderSection = (section: PageSection) => {
    const key = `section-${section.id}`;

    switch (section.type as string) {
      case 'hero':
        return <HeroSection key={key} config={section.config} locale={locale} />;
      case 'features':
        return <FeaturesSection key={key} config={section.config} locale={locale} />;
      case 'pricing':
        return <PricingSection key={key} config={section.config} locale={locale} currency={currency} />;
      case 'testimonials':
        return <TestimonialsSection key={key} config={section.config} locale={locale} />;
      case 'stats':
        return <StatsSection key={key} config={section.config} />;
      case 'cta':
        return <CTASection key={key} config={section.config} locale={locale} />;
      case 'logo-cloud':
        return <LogoCloudSection key={key} config={section.config} />;
      case 'faq':
        return <FAQSection key={key} config={section.config} />;
      case 'content':
        return <ContentSection key={key} config={section.config} />;
      case 'form':
        return <FormSection key={key} config={section.config} locale={locale} />;
      case 'how-it-works':
        return <HowItWorksSection key={key} config={section.config} locale={locale} />;
      case 'newsletter':
        return <NewsletterSection key={key} config={section.config} locale={locale} />;
      case 'whitepaper-download':
        return <WhitepaperDownloadSection key={key} config={section.config} locale={locale} />;
      case 'objection-handling':
        return <ObjectionHandlingSection key={key} config={section.config} />;
      case 'trust-stack':
        return <TrustStackSection key={key} config={section.config} />;
      case 'pricing-faq':
        return <FAQSection key={key} config={section.config} />;
      default:
        console.warn(`Unknown section type: ${section.type}`);
        return null;
    }
  };

  return (
    <div className="public-page">
      {sections.sort((a, b) => a.order - b.order).map(renderSection)}
    </div>
  );
}
